import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Building2, Check, Smartphone } from "lucide-react";

import { useAudio } from "../../../../../lib/use-audio";
import { KioskStep } from "../../../../../components/kiosk-step";
import { KioskStatePanel } from "../../../../../components/kiosk-state-panel";
import { useCheckInContext, useCheckInSend } from "../context";
import type { EebKioskConsentAcceptance } from "@ascleo/frontdesk-contracts/eeb/kiosk";

/** Seconds the acknowledgement stays before the flow continues on its own. */
const ACKNOWLEDGEMENT_SECONDS = 10;

/**
 * Truthful acknowledgement after the signed kiosk endpoint durably accepts an eEB request.
 *
 * This screen never claims insurer delivery or coverage confirmation. Staff-mediated requests
 * acknowledge the queue handoff; automatic requests acknowledge only that immediate processing
 * was accepted. The patient can finish right away; otherwise the flow continues after a visible
 * countdown (KC-39).
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <EebRequestAcknowledgement acceptance={acceptance} insurerName="Techniker Krankenkasse" />
 * ```
 */
function EebRequestAcknowledgement({
  acceptance,
  insurerName,
}: {
  acceptance: EebKioskConsentAcceptance;
  insurerName: string;
}) {
  const { t } = useTranslation();
  const send = useCheckInSend();
  const [secondsLeft, setSecondsLeft] = useState(ACKNOWLEDGEMENT_SECONDS);

  useEffect(
    function countDownAcknowledgement() {
      const timer = globalThis.setInterval(() => {
        setSecondsLeft((current) => Math.max(0, current - 1));
      }, 1_000);
      return function stopAcknowledgementCountdown() {
        globalThis.clearInterval(timer);
      };
    },
    [],
  );

  useEffect(
    function finishCheckInAfterTruthfulAcknowledgement() {
      if (secondsLeft === 0) send({ type: "EEB_OK" });
    },
    [secondsLeft, send],
  );

  const staffMediated = acceptance.submissionMode === "staff-mediated";

  return (
    <KioskStep
      width="narrow"
      align="center"
      title={t("self-service.check-in.eeb-request.title")}
      lead={t("self-service.check-in.eeb-request.subtitle", {
        insurer: insurerName,
      })}
      primaryAction={{
        label: t("common.done"),
        icon: Check,
        tone: "success",
        onClick: () => send({ type: "EEB_OK" }),
        testId: "frontdesk-eeb-request-finish-btn",
      }}
    >
      <output
        data-testid="frontdesk-eeb-request-acknowledgement"
        className="block w-full"
      >
        <KioskStatePanel
          tone="success"
          title={
            staffMediated
              ? t("self-service.check-in.eeb-request.success")
              : t("self-service.check-in.eeb-request.verifying")
          }
          body={t("self-service.check-in.eeb-request.non-blocking-note")}
        >
          <p className="m-0 text-base text-foreground" aria-live="off">
            {t("self-service.check-in.eeb-request.auto-continue", {
              seconds: secondsLeft,
            })}
          </p>
        </KioskStatePanel>
      </output>
    </KioskStep>
  );
}

/**
 * Shown when this step is reached without a durable acceptance.
 *
 * There is no request to acknowledge here, so nothing may suggest one was
 * recorded: no success wording and no success iconography. The patient gets
 * two clear ways on: the insurance-app QR route, or clearing it at the
 * reception (KC-41).
 *
 * @since 1.1.0
 * @example
 * ```tsx
 * <EebRequestUnavailable />
 * ```
 */
function EebRequestUnavailable() {
  const { t } = useTranslation();
  const send = useCheckInSend();

  return (
    <KioskStep
      width="narrow"
      align="center"
      title={t("self-service.check-in.eeb-request.unavailable-title")}
    >
      <KioskStatePanel
        tone="error"
        testId="frontdesk-eeb-request-unavailable"
        title={t("self-service.check-in.eeb-request.unavailable-v2")}
        body={t("self-service.check-in.eeb-request.unavailable-body")}
        action={{
          label: t("self-service.check-in.eeb-request.use-app"),
          icon: Smartphone,
          onClick: () => send({ type: "EEB_FAIL" }),
          testId: "frontdesk-eeb-request-alternative-button",
        }}
        secondaryAction={{
          label: t("self-service.check-in.eeb-request.at-reception"),
          icon: Building2,
          onClick: () => send({ type: "SKIP_EEB" }),
          testId: "frontdesk-eeb-request-reception-button",
        }}
      />
    </KioskStep>
  );
}

/**
 * eEB request step: acknowledges a durable acceptance, or offers a way out.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <EebRequestStep />
 * ```
 */
export function EebRequestStep() {
  const { t } = useTranslation();
  const context = useCheckInContext();
  const acceptance = context.eebAcceptance;

  useAudio("checkIn-eebRequest");

  if (acceptance === undefined) {
    return <EebRequestUnavailable />;
  }

  return (
    <EebRequestAcknowledgement
      acceptance={acceptance}
      insurerName={
        context.selectedInsurerName ??
        context.previousInsurerName ??
        t("self-service.check-in.eeb-consent.insurance")
      }
    />
  );
}
